import * as React from 'react';
import { createRef, ReactNode, RefObject, useContext, useEffect, useState } from 'react';
import { Layout } from '../Layout';
import { IntlShape, useIntl } from 'react-intl';
import { ImageServiceApi, StoreImageRequest, UsersDTO } from '../../api/admin';
import { AuthContext } from '../../Auth/components/AuthProvider';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';
import { Loading } from '../system/Loading';
import { Divider } from 'primereact/divider';
import { StateContext } from '../../util/providers/StateProvider';
import { getMandantSettingsByUsername } from '../../util/mandant-settings-utils';
import { OrcidNotLinked } from './orcid/OrcidNotLinked';
import { OrcidScopes } from '../../Settings/orcid';
import { HTTPQuery } from '../../api/core';
import { AuthenticateApi } from '../../api/orcid';
import { ConnectOrcidFormData } from './orcid/ConnectOrcidFormData';
import { OrcidLinked } from './orcid/OrcidLinked';
import { ToastContext, ToastContextType } from '../../util/providers/ToastProvider';
import { MandantSettings } from '../../util/types';
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import { UserAvatarComponent } from '../components/UserAvatar';
import { maxImageSizeMb } from '../../Settings/config';
import { Button } from 'primereact/button';

  export const SettingsPage: React.FC = (): ReactNode => {
  const { actions, user, loadAvatar, avatar } = useContext(AuthContext);
  const imageApi: ImageServiceApi = new ImageServiceApi().withMiddleware(getAuthMiddleware(actions));
  const authApi: AuthenticateApi = new AuthenticateApi().withMiddleware(getAuthMiddleware(actions));
  const intl: IntlShape = useIntl();
  const fileUploadRef: RefObject<FileUpload> = createRef<FileUpload>();
  const [userDto, setUserDto] = useState<UsersDTO>(user);
  const toast: ToastContextType = useContext(ToastContext);
  const {originMandantSettings} = useContext(StateContext);
  const [userMandant, setUserMandant] = useState<MandantSettings>(null);

  useEffect((): void => {
    if(userDto?.username){
      setUserMandant(getMandantSettingsByUsername(userDto.username));
    }
  }, [userDto]);


  const authorize: (connectOrcidFormData: ConnectOrcidFormData) => Promise<void> = async (connectOrcidFormData: ConnectOrcidFormData): Promise<void> => {
    const token: string = (await actions.getAuthTokenOrRefresh(true) ?? '').replace('Bearer ', '');
    const redirectUrl: string = window.location.href;
    const institution: string = getMandantSettingsByUsername(userDto.username)?.institution;
    const scopes: string[] = [];

    if (connectOrcidFormData.read_limited) {
      scopes.push(OrcidScopes.READ_LIMITED);
    }
    if (connectOrcidFormData.activities_update) {
      scopes.push(OrcidScopes.ACTIVITIES_UPDATE);
    }

    if (institution === null || institution === undefined) {
      throw new Error('Required parameter requestParameters.institutionId was null or undefined when calling authenticate.');
    }
    if (userDto.username === null || userDto.username === undefined) {
      throw new Error('Required parameter requestParameters.institutionalResearcherId was null or undefined when calling authenticate.');
    }
    if (redirectUrl === null || redirectUrl === undefined) {
      throw new Error('Required parameter requestParameters.redirectUrl was null or undefined when calling authenticate.');
    }
    const queryParameters: HTTPQuery = {};
    if (connectOrcidFormData.publicUsageOfLimitedOrcidData !== undefined) {
      queryParameters.allow_public_usage_of_limited_orcid_data = encodeURIComponent(connectOrcidFormData.publicUsageOfLimitedOrcidData);
    }
    queryParameters.institution_id = encodeURIComponent(institution);
    queryParameters.institutional_researcher_id = encodeURIComponent(userDto.username);
    queryParameters.scope = scopes.map((scope) => encodeURIComponent(scope));
    queryParameters.redirect_url = encodeURIComponent(redirectUrl);
    window.location.href = `https://${window.location.hostname}/mms3-orcid/orcid/auth/authenticate?${Object.keys(queryParameters).map(key => key + '=' + queryParameters[key].toString()).join('&')}&access_token=${token}`;
  };

  const deleteToken: () => void = (): void => {
    void authApi
      .deleteToken({ orcidId: userDto.orcidAuthorize.orcidID })
      .then((): void => setUserDto({ ...userDto, orcidAuthorize: { ...userDto.orcidAuthorize, tokenRevoked: true } }))
      .catch((e): void => toast.error(e));
  };

  const submitAvatar: (event: FileUploadHandlerEvent) => void = (event: FileUploadHandlerEvent): void => {
    void fetch(event.files[0].objectURL)
      .then((response: Response): Promise<Blob> => response.blob())
      .then((imgBlob: Blob): StoreImageRequest => ({ userId: user.id, image: imgBlob }))
      .then((params: StoreImageRequest): Promise<void> => imageApi.storeImage(params))
      .then((): void => {
        fileUploadRef.current.clear();
        loadAvatar();
      });
  };

  const deleteAvatar: () => void = (): void => void imageApi.deleteImage({ userId: user.id }).then(loadAvatar);

  if (!userDto) {
    return <Loading/>;
  }
  return (
    <Layout title={intl.formatMessage({ id: 'userMenu.settings' })}>
      <div className={'mx-auto px-3'} style={{ maxWidth: '50rem' }}>
        <h2>{intl.formatMessage({ id: 'settings.tab.profile' })}</h2>
        <h3>{intl.formatMessage({ id: 'settings.profile.avatar' })}</h3>
        <div>
          <div className={'flex flex-column gap-3'}>
            <div className={'w-10rem h-10rem'}>
              <UserAvatarComponent type={'large'} currentUser={true} />
            </div>
            <div className={'flex gap-3 flex-wrap'}>
              <FileUpload
                mode={'basic'}
                name="avatar"
                multiple={false}
                ref={fileUploadRef}
                auto={true}
                accept="image/jpg,image/jpeg,image/png"
                customUpload={true}
                uploadHandler={submitAvatar}
                maxFileSize={maxImageSizeMb * 1000000}
                chooseLabel={intl.formatMessage({ id: 'settings.profile.avatar.change' })}
              />
              <Button
                icon={'pi pi-minus'}
                disabled={!avatar}
                severity={'danger'}
                outlined={true}
                label={intl.formatMessage({ id: 'settings.profile.avatar.delete' })}
                name="deleteAvatar"
                onClick={deleteAvatar}
              />
            </div>
            <div className={'flex flex-column gap-1'}>
              <span>{intl.formatMessage({ id: 'settings.profile.avatar.fileTypes' })}</span>
              <span>{intl.formatMessage({ id: 'settings.profile.avatar.maxSize' })}</span>
            </div>
          </div>
        </div>
        <Divider />
        <h3>{intl.formatMessage({ id: 'settings.profile.main' })}</h3>
        <div className={'flex flex-column gap-1'}>
          <div className={'flex'}>
            <div className={'w-4'}>
              <span>{intl.formatMessage({ id: 'settings.profile.main.givenname' })}:</span>
            </div>
            <div className={'w-8'}>
              <span>{userDto.givenname}</span>
            </div>
          </div>
          <div className={'flex'}>
            <div className={'w-4'}>
              <span>{intl.formatMessage({ id: 'settings.profile.main.surname' })}:</span>
            </div>
            <div className={'w-8'}>
              <span>{userDto.surname}</span>
            </div>
          </div>
          <div className={'flex'}>
            <div className={'w-4'}>
              <span>{intl.formatMessage({ id: 'settings.profile.main.email' })}:</span>
            </div>
            <div className={'w-8'}>
              <span>{userDto.username}</span>
            </div>
          </div>
        </div>
        {userMandant?.usesOrcid && originMandantSettings?.usesOrcid && (
          <>
            <Divider />
            <h2>{intl.formatMessage({ id: 'settings.tab.orcid' })}</h2>
            {!!userDto.orcidAuthorize && (
              <OrcidLinked
                orcidAuthorize={userDto.orcidAuthorize}
                revoke={deleteToken}
                submit={authorize}
                mandant={userMandant.mandant}
              />
            )}
            {!userDto.orcidAuthorize && <OrcidNotLinked submit={authorize} mandant={userMandant.mandant} />}
          </>
        )}
      </div>
    </Layout>
  );
};

export default SettingsPage;
